import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Reveal, SectionTitle, Stars } from "../components/shared";
import { InquiryForm } from "../components/InquiryForm";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Clock, Baby, CalendarCheck, ArrowRight, Palette, Sprout, ChefHat, Heart, Globe2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const SOCCER = "https://images.pexels.com/photos/10885468/pexels-photo-10885468.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function Home() {
  const { t } = useI18n();
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const handleVideoReady = () => {
    setVideoReady(true);
    if (videoRef.current) videoRef.current.playbackRate = 0.75;
  };
  const [site, setSite] = useState({});
  const [programs, setPrograms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/site").then(({ data }) => setSite(data)).catch(() => {});
    api.get("/programs").then(({ data }) => setPrograms(data)).catch(() => {});
    api.get("/teachers").then(({ data }) => setTeachers(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {});
  }, []);

  const featured = programs.filter((p) => p.featured);
  const icons = { atelier: Palette, garden: Sprout, cooking: ChefHat };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end bg-navy-dark">
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
          autoPlay muted loop playsInline preload="auto"
          onCanPlay={handleVideoReady}
          onLoadedData={handleVideoReady}
          data-testid="hero-video"
        >
          <source src={`${process.env.PUBLIC_URL}/hero.mp4`} type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 pb-20 pt-32 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-mustard text-navy text-sm font-semibold mb-5">Now Enrolling</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight drop-shadow-md">
              {t.hero.title}
            </h1>
            <div className="mt-8 flex flex-wrap gap-3">
              <InquiryForm type="tour" trigger={<Button size="lg" className="rounded-full bg-coral hover:bg-coral-dark text-white h-13 px-7 text-base shadow-lg" data-testid="hero-book-tour-btn">{t.hero.cta1}</Button>} />
              <Button asChild size="lg" variant="outline" className="rounded-full h-13 px-7 text-base bg-white/10 border-white/40 text-white hover:bg-white hover:text-navy backdrop-blur-sm" data-testid="hero-programs-btn">
                <Link to="/programs">{t.hero.cta2} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INFO BAR */}
      <section className="max-w-7xl mx-auto px-6 pt-16 relative z-10">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Clock, label: t.info.hours, value: site.hours },
            { icon: Baby, label: t.info.ages, value: site.ages },
            { icon: CalendarCheck, label: t.info.availability, value: site.availability },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] flex items-start gap-4 h-full">
                <div className="h-11 w-11 rounded-xl bg-coral/10 flex items-center justify-center shrink-0"><c.icon className="h-5 w-5 text-coral" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-navy/50 font-semibold">{c.label}</div>
                  <div className="font-heading font-semibold text-navy mt-1 leading-snug">{c.value}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SIGNATURE PROGRAMS BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><SectionTitle eyebrow="What makes us special" title={t.sections.signatureTitle} subtitle={t.sections.signatureSub} /></Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {featured.map((p, i) => {
            const Icon = icons[p.slug] || Palette;
            return (
              <Reveal key={p.id} delay={i * 0.12}>
                <Link to="/programs" className="group block relative rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)] min-h-[420px] h-full" data-testid={`home-program-${p.slug}`}>
                  <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-6 text-white">
                    <div className="h-11 w-11 rounded-xl bg-mustard flex items-center justify-center text-navy mb-3"><Icon className="h-5 w-5" /></div>
                    <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                    <p className="text-sm text-white/85 mt-2 leading-relaxed">{p.summary}</p>
                    <span className="mt-3 text-mustard font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SOCCER — NEW PROGRAM */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden bg-navy text-white grid lg:grid-cols-2 shadow-xl">
            <div className="p-10 sm:p-14 flex flex-col justify-center order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mustard text-navy text-xs font-semibold uppercase tracking-widest w-fit"><Sparkles className="h-3.5 w-3.5" /> New · Coming Soon</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">Weekly Soccer Program</h2>
              <p className="mt-4 text-cream/80 text-lg leading-relaxed">We're bringing a dedicated soccer coach to Ainalem once a week! Little ones will build coordination, teamwork, and confidence through playful, age-appropriate games — all in a safe, joyful setting.</p>
              <div className="mt-6">
                <InquiryForm type="enrollment" defaultProgram="Weekly Soccer Program" title="Soccer Program Interest"
                  trigger={<Button className="rounded-full bg-coral hover:bg-coral-dark text-white h-12 px-7" data-testid="soccer-interest-btn">Register Interest</Button>} />
              </div>
            </div>
            <div className="relative min-h-[280px] order-1 lg:order-2">
              <img src={SOCCER} alt="Children playing soccer" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </Reveal>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src="https://images.unsplash.com/photo-1761604478724-13fe879468cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Teacher with children" className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]" />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="text-coral font-semibold tracking-widest uppercase text-xs mb-3">Our approach</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy tracking-tight">{t.sections.philosophyTitle}</h2>
            <p className="mt-5 text-lg text-navy/70 leading-relaxed">
              We believe every child is capable, curious, and full of potential. Inspired by the Reggio-Emilia philosophy, we follow children's interests, honor their pace, and create a warm environment where art, nature, and nourishing food are part of everyday learning.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {[
                { icon: Heart, t: "Child-led learning", d: "We follow each child's curiosity and pace." },
                { icon: Sprout, t: "Nature & the outdoors", d: "Daily time in our garden and outdoor classroom." },
                { icon: ChefHat, t: "Fresh, healthy meals", d: "Nutritious food, often grown & cooked together." },
                { icon: Globe2, t: "Belonging for all", d: "A diverse community where every family fits in." },
              ].map((f, i) => (
                <div key={i} className="flex gap-3">
                  <div className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center shrink-0"><f.icon className="h-5 w-5 text-navy" /></div>
                  <div><div className="font-heading font-semibold text-navy">{f.t}</div><div className="text-sm text-navy/60 mt-0.5">{f.d}</div></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TEACHERS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><SectionTitle eyebrow="Our people" title={t.sections.teachersTitle} center /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teachers.map((tc, i) => (
            <Reveal key={tc.id} delay={i * 0.1}>
              <div className="text-center group" data-testid={`teacher-${i}`}>
                <div className="rounded-3xl overflow-hidden aspect-[4/5] mb-4 shadow-md">
                  <img src={tc.image} alt={tc.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="font-heading font-bold text-navy text-lg">{tc.name}</h3>
                <div className="text-coral text-sm font-semibold">{tc.role}</div>
                <p className="text-sm text-navy/60 mt-2 leading-relaxed">{tc.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy py-24 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><div className="text-mustard font-semibold tracking-widest uppercase text-xs mb-3 text-center">{t.sections.testimonialsTitle}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-14">Trusted by families like yours</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.id} delay={i * 0.08}>
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm h-full" data-testid={`testimonial-${i}`}>
                  <Stars n={tm.rating} />
                  <p className="mt-4 text-lg leading-relaxed text-cream/90">“{tm.quote}”</p>
                  <div className="mt-5 font-heading font-semibold text-mustard">{tm.name}</div>
                  <div className="text-sm text-cream/60">{tm.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-24">
        <Reveal><SectionTitle eyebrow="Good to know" title={t.sections.faqTitle} center /></Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="bg-white rounded-2xl border border-black/5 px-5 shadow-sm" data-testid={`faq-${f.order}`}>
                <AccordionTrigger className="font-heading font-semibold text-navy text-left hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-navy/70 leading-relaxed">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <Reveal>
          <div className="rounded-[2rem] bg-coral text-white p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
            <div className="absolute -left-10 -bottom-16 h-64 w-64 rounded-full bg-mustard/20" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold">{t.sections.ctaTitle}</h2>
              <p className="mt-3 text-white/90 text-lg max-w-xl mx-auto">{t.sections.ctaSub}</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <InquiryForm type="tour" trigger={<Button size="lg" className="rounded-full bg-white text-coral hover:bg-cream h-13 px-8 text-base font-semibold" data-testid="cta-book-tour-btn">{t.nav.bookTour}</Button>} />
                <InquiryForm type="enrollment" trigger={<Button size="lg" variant="outline" className="rounded-full h-13 px-8 text-base border-white/60 bg-transparent text-white hover:bg-white hover:text-coral" data-testid="cta-enroll-btn">{t.info.enrollCta}</Button>} />
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
